// ============================================
// PostProcessStack - Post-processing effect management
// ============================================

import type { IRenderContext } from '../core/IRenderContext';
import { OffscreenTarget } from '../core/RenderPass';
import {
  type TextureHandle,
  type RenderTargetHandle,
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../types';
import type { Camera2D } from '../objects/Camera2D';

// Blit shader (Slang → WGSL, compiled into ShaderCache at engine startup).
import { getCompiledShader } from './ShaderCache';

/**
 * Post-processing effect context
 */
export interface PostProcessContext {
  ctx: IRenderContext;
  camera: Camera2D;
  /** Source texture to read from */
  source: TextureHandle;
  /** Destination render target (or null for backbuffer) */
  destination: RenderTargetHandle | null;
  width: number;
  height: number;
  time: number;
  deltaTime: number;
  /** Effect intensity (0-1), can be animated for transitions */
  intensity: number;
  /** Whether the destination is an HDR target (rgba16float). False for screen output (bgra8unorm) */
  isHdrTarget: boolean;
}

/**
 * Desired state of one effect, as stored on the camera component. Fed to {@link PostProcessStack.reconcile}
 * to sync the live stack in place.
 */
export interface PostProcessEffectDesc {
  type: string;
  enabled?: boolean;
  intensity?: number;
  order?: number;
  params?: Record<string, unknown>;
}

/**
 * Serializable parameter definition for post-process effects
 */
export interface PostProcessParameter {
  name: string;
  type: 'float' | 'int' | 'bool' | 'color' | 'vector2' | 'vector3' | 'vector4';
  min?: number;
  max?: number;
  default: number | boolean | number[];
  description?: string;
}

/**
 * Base class for post-processing effects
 */
export abstract class PostProcessEffect {
  /** Unique type identifier for serialization */
  abstract readonly type: string;
  
  /** Display name for UI */
  abstract readonly displayName: string;
  
  /** Whether the effect is enabled */
  enabled = true;
  
  /** Effect intensity (0-1) */
  intensity = 1.0;
  
  /** Execution order (lower = earlier) */
  order = 0;
  
  /** Internal init state */
  protected _initialized = false;

  /**
   * Check if the effect requires initialization
   */
  get needsInit(): boolean {
    return !this._initialized;
  }

  /**
   * Whether the effect can actually render this frame. Effects that need async resources
   * (e.g. a texture fetched over the network) override this; while false the stack treats the
   * effect as inactive, so the chain keeps flowing instead of the effect swallowing the frame
   * (an effect that draws nothing leaves its destination cleared → black screen).
   */
  get isReady(): boolean {
    return true;
  }
  
  /**
   * Initialize GPU resources
   */
  initialize(ctx: IRenderContext): void {
    if (this._initialized) return;
    this.onInitialize(ctx);
    this._initialized = true;
  }
  
  /**
   * Render the effect
   */
  render(context: PostProcessContext): void {
    if (!this.enabled || this.intensity <= 0) return;
    if (!this._initialized) {
      this.initialize(context.ctx);
    }
    this.onRender({ ...context, intensity: context.intensity * this.intensity });
  }
  
  /**
   * Handle viewport resize
   */
  resize(ctx: IRenderContext, width: number, height: number): void {
    if (this._initialized) {
      this.onResize(ctx, width, height);
    }
  }
  
  /**
   * Cleanup GPU resources
   */
  dispose(ctx: IRenderContext): void {
    if (!this._initialized) return;
    this.onDispose(ctx);
    this._initialized = false;
  }
  
  /**
   * Get parameter definitions for inspector UI
   */
  abstract getParameters(): PostProcessParameter[];
  
  /**
   * Get parameter value by name
   */
  abstract getParameterValue(name: string): number | boolean | number[];
  
  /**
   * Set parameter value by name
   */
  abstract setParameterValue(name: string, value: number | boolean | number[]): void;
  
  /**
   * Serialize effect state to JSON
   */
  serialize(): Record<string, unknown> {
    const data: Record<string, unknown> = {
      type: this.type,
      enabled: this.enabled,
      intensity: this.intensity,
      order: this.order,
    };
    
    for (const param of this.getParameters()) {
      data[param.name] = this.getParameterValue(param.name);
    }
    
    return data;
  }
  
  /**
   * Deserialize effect state from JSON
   */
  deserialize(data: Record<string, unknown>): void {
    if (data.enabled !== undefined) this.enabled = data.enabled as boolean;
    if (data.intensity !== undefined) this.intensity = data.intensity as number;
    if (data.order !== undefined) this.order = data.order as number;
    
    for (const param of this.getParameters()) {
      if (data[param.name] !== undefined) {
        this.setParameterValue(param.name, data[param.name] as number | boolean | number[]);
      }
    }
  }
  
  // ============================================
  // Override in subclasses
  // ============================================

  protected abstract onInitialize(ctx: IRenderContext): void;
  protected abstract onRender(context: PostProcessContext): void;
  protected abstract onDispose(ctx: IRenderContext): void;

  protected onResize(ctx: IRenderContext, width: number, height: number): void {
    // Default: no-op
  }

  /**
   * Issue one fullscreen-triangle draw with per-draw-isolated uniforms.
   *
   * The order here is load-bearing (see IRenderContext.captureUniforms):
   * `setUniform` needs the pipeline bound to resolve the 'packed' layout, and the snapshot must be
   * captured BEFORE the last `bindTexture` — that's when the bind group is built and it latches
   * whichever uniform buffer is current. Without the snapshot every draw shares ONE uniform buffer,
   * and since `queue.writeBuffer` runs at queue-timeline order (before the frame's command buffer
   * is submitted), all passes recorded in the same submit read the LAST pass's uniform values —
   * e.g. bloom's threshold/downsample chain silently ran on the composite pass's uniforms.
   */
  protected fullscreenPass(
    ctx: IRenderContext,
    pipeline: PipelineHandle,
    packed: Float32Array | null,
    textures: readonly TextureHandle[],
  ): void {
    ctx.setPipeline(pipeline);
    if (packed) {
      ctx.setUniform('packed', packed);
      ctx.captureUniforms?.();
    }
    for (let i = 0; i < textures.length; i++) {
      ctx.bindTexture(i, textures[i]);
    }
    ctx.draw(3);
  }
}

/**
 * Post-processing stack - manages a list of effects with ping-pong rendering
 */
export class PostProcessStack {
  private effects: PostProcessEffect[] = [];
  private ctx: IRenderContext | null = null;
  
  // Ping-pong buffers for multi-pass rendering
  private pingTarget: OffscreenTarget | null = null;
  private pongTarget: OffscreenTarget | null = null;
  private currentWidth = 0;
  private currentHeight = 0;

  // Blit pipeline for copying textures when no effects active
  private blitShader: ShaderHandle = NULL_HANDLE;
  private blitPipelineLDR: PipelineHandle = NULL_HANDLE;
  private blitPipelineHDR: PipelineHandle = NULL_HANDLE;

  private warnedDuplicateTonemap = false;
  
  /** Whether the stack has any enabled effects */
  get hasActiveEffects(): boolean {
    return this.effects.some(e => e.enabled && e.intensity > 0 && e.isReady);
  }
  
  /** Number of effects in the stack */
  get effectCount(): number {
    return this.effects.length;
  }
  
  /**
   * Initialize the stack with a render context
   */
  initialize(ctx: IRenderContext, width: number, height: number): void {
    this.ctx = ctx;
    this.initBlitPipeline(ctx);
    this.resize(width, height);
  }

  /**
   * Initialize blit shader and pipelines (LDR + HDR variants)
   */
  private initBlitPipeline(ctx: IRenderContext): void {
    if (this.blitShader !== NULL_HANDLE) return;

    const blitWgsl = getCompiledShader('blit');
    this.blitShader = ctx.createShader(blitWgsl, blitWgsl);

    // LDR pipeline (swap chain format)
    this.blitPipelineLDR = ctx.createPipeline({
      shader: this.blitShader,
      vertexBuffers: [],
      colorTargets: [{ format: ctx.getSwapChainFormat() }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'PostProcessStack-blit-ldr',
    });

    // HDR pipeline (rgba16float)
    this.blitPipelineHDR = ctx.createPipeline({
      shader: this.blitShader,
      vertexBuffers: [],
      colorTargets: [{ format: 'rgba16float' }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'PostProcessStack-blit-hdr',
    });
  }
  
  /**
   * Handle viewport resize
   */
  resize(width: number, height: number): void {
    if (!this.ctx) return;
    if (width === this.currentWidth && height === this.currentHeight) return;
    
    this.currentWidth = width;
    this.currentHeight = height;

    // Drop the ping-pong targets; they're recreated lazily at the new size, and only when a frame
    // actually chains ≥2 active effects. Saves two full-res rgba16float targets (~33 MB @1080p) in
    // the common 0–1 effect case — e.g. bloom-only, which manages its own targets and renders
    // straight to the destination without ever sampling ping/pong.
    this.pingTarget?.destroy();
    this.pingTarget = null;
    this.pongTarget?.destroy();
    this.pongTarget = null;

    // Notify effects of resize
    for (const effect of this.effects) {
      effect.resize(this.ctx, width, height);
    }
  }

  /** Lazily (re)create the ping-pong targets at the current size. Called only when ≥2 effects chain. */
  private ensurePingPong(): boolean {
    if (!this.ctx) return false;
    if (!this.pingTarget) {
      this.pingTarget = new OffscreenTarget(this.ctx, this.currentWidth, this.currentHeight, false, true);
    }
    if (!this.pongTarget) {
      this.pongTarget = new OffscreenTarget(this.ctx, this.currentWidth, this.currentHeight, false, true);
    }
    return true;
  }
  
  /**
   * Add an effect to the stack
   */
  addEffect(effect: PostProcessEffect): void {
    this.effects.push(effect);
    this.sortEffects();
    
    // Initialize if we have a context
    if (this.ctx && effect.needsInit) {
      effect.initialize(this.ctx);
    }
  }
  
  /**
   * Remove an effect from the stack
   */
  removeEffect(effect: PostProcessEffect): boolean {
    const idx = this.effects.indexOf(effect);
    if (idx >= 0) {
      if (this.ctx) {
        effect.dispose(this.ctx);
      }
      this.effects.splice(idx, 1);
      return true;
    }
    return false;
  }
  
  /**
   * Remove effect by type
   */
  removeEffectByType(type: string): boolean {
    const idx = this.effects.findIndex(e => e.type === type);
    if (idx >= 0) {
      if (this.ctx) {
        this.effects[idx].dispose(this.ctx);
      }
      this.effects.splice(idx, 1);
      return true;
    }
    return false;
  }
  
  /**
   * Get effect by type
   */
  getEffect<T extends PostProcessEffect>(type: string): T | null {
    return this.effects.find(e => e.type === type) as T | null;
  }
  
  /**
   * Get all effects
   */
  getEffects(): readonly PostProcessEffect[] {
    return this.effects;
  }
  
  /**
   * Clear all effects from the stack
   */
  clear(): void {
    if (this.ctx) {
      for (const effect of this.effects) {
        effect.dispose(this.ctx);
      }
    }
    this.effects = [];
  }

  /**
   * Reconcile the stack against a list of effect descriptors IN PLACE, reusing existing effect
   * instances by type instead of disposing and rebuilding every effect. This is the difference
   * between an animated post-process parameter costing one setParameterValue per frame vs.
   * destroying and recreating every shader/pipeline/target — bloom's entire mip chain included —
   * on every frame the value changes.
   *
   * @param descs Desired effects (type + enabled/intensity/order/params), in any order.
   * @param createEffect Factory for effect types not already present. Passed in (rather than
   *   imported) so this package stays independent of the effect registry and avoids an import cycle.
   */
  reconcile(
    descs: ReadonlyArray<PostProcessEffectDesc>,
    createEffect: (type: string) => PostProcessEffect | null,
  ): void {
    // Pool existing effects by type so repeated types map stably (FIFO within a type).
    const pool = new Map<string, PostProcessEffect[]>();
    for (const e of this.effects) {
      const arr = pool.get(e.type);
      if (arr) arr.push(e);
      else pool.set(e.type, [e]);
    }

    const next: PostProcessEffect[] = [];
    for (const d of descs) {
      let effect = pool.get(d.type)?.shift();
      if (!effect) {
        const created = createEffect(d.type);
        if (!created) {
          console.warn(`[PostProcessStack] Unknown effect type: ${d.type}`);
          continue;
        }
        effect = created;
        if (this.ctx) {
          if (effect.needsInit) effect.initialize(this.ctx);
          // Build size-dependent resources (e.g. the bloom mip chain) for the freshly added effect.
          effect.resize(this.ctx, this.currentWidth, this.currentHeight);
        }
      }

      effect.enabled = d.enabled ?? true;
      effect.intensity = d.intensity ?? 1;
      effect.order = d.order ?? 0;
      if (d.params) {
        for (const [name, value] of Object.entries(d.params)) {
          if (
            typeof value === 'number' ||
            typeof value === 'boolean' ||
            (Array.isArray(value) && value.every(v => typeof v === 'number'))
          ) {
            effect.setParameterValue(name, value as number | boolean | number[]);
          }
        }
      }
      next.push(effect);
    }

    // Dispose effects that were present before but are absent now (left unclaimed in the pool).
    if (this.ctx) {
      for (const arr of pool.values()) {
        for (const e of arr) e.dispose(this.ctx);
      }
    }

    this.effects = next;
    this.sortEffects();
  }
  
  /**
   * Move effect up in stack (earlier execution)
   */
  moveEffectUp(effect: PostProcessEffect): boolean {
    const idx = this.effects.indexOf(effect);
    if (idx > 0) {
      // Swap orders
      const prev = this.effects[idx - 1];
      const temp = effect.order;
      effect.order = prev.order;
      prev.order = temp;
      this.sortEffects();
      return true;
    }
    return false;
  }
  
  /**
   * Move effect down in stack (later execution)
   */
  moveEffectDown(effect: PostProcessEffect): boolean {
    const idx = this.effects.indexOf(effect);
    if (idx >= 0 && idx < this.effects.length - 1) {
      // Swap orders
      const next = this.effects[idx + 1];
      const temp = effect.order;
      effect.order = next.order;
      next.order = temp;
      this.sortEffects();
      return true;
    }
    return false;
  }
  
  /**
   * Execute all enabled effects with ping-pong rendering
   *
   * @param sourceTexture Initial source texture (e.g., scene render)
   * @param destinationTarget Final destination (null = backbuffer)
   * @param camera Current camera
   * @param time Game time
   * @param deltaTime Frame delta
   * @param profile Optional per-effect profiling hook: `(label, run) => run()`. Supplied by the caller so this
   *   package stays profiler-agnostic (render2d has no @engine/core dependency). Each effect is wrapped in a
   *   `Render.PostProcess.<type>` scope; omit for no instrumentation.
   * @param opts.finalTonemap Builtin HDR→LDR resolve supplied by an HDR camera. Used only when the stack has no
   *   active tonemap of its own; a user tonemap (its operator/exposure) always wins. The instance is owned by the
   *   caller — it must live OUTSIDE this stack's effects so reconcile() never disposes it.
   */
  render(
    sourceTexture: TextureHandle,
    destinationTarget: RenderTargetHandle | null,
    camera: Camera2D,
    time: number,
    deltaTime: number,
    profile?: (label: string, run: () => void) => void,
    opts?: { finalTonemap?: PostProcessEffect | null }
  ): void {
    if (!this.ctx) {
      console.warn('[PostProcessStack] Cannot render: missing ctx');
      return;
    }

    let activeEffects = this.effects.filter(e => e.enabled && e.intensity > 0 && e.isReady);

    // Tonemap terminates the chain no matter where its authored `.order` puts it — every effect after a
    // tonemap would run on clamped LDR data and silently break (bloom can't see >1, grading shifts twice).
    // Enforced at render time only; the serialized order is left alone so the inspector doesn't fight the user.
    const userTonemaps = activeEffects.filter(e => e.type === 'tonemap');
    if (userTonemaps.length > 1 && !this.warnedDuplicateTonemap) {
      this.warnedDuplicateTonemap = true;
      console.warn('[PostProcessStack] Multiple tonemap effects active; only the last one (by order) is applied.');
    }
    const resolveTonemap = userTonemaps.length > 0
      ? userTonemaps[userTonemaps.length - 1]
      : opts?.finalTonemap ?? null;
    if (resolveTonemap) {
      activeEffects = activeEffects.filter(e => e.type !== 'tonemap');
      activeEffects.push(resolveTonemap);
    }

    if (activeEffects.length === 0) {
      // No effects - just blit source to destination
      this.blitToTarget(sourceTexture, destinationTarget);
      return;
    }

    // Ping-pong targets are only needed to chain the intermediate effects (every effect but the last).
    // A single effect renders straight to the destination, so it never touches them.
    if (activeEffects.length >= 2 && !this.ensurePingPong()) return;

    // Setup ping-pong
    let currentSource = sourceTexture;
    let usePing = true;
    
    // Process all effects except last. The loop only runs when length >= 2, so ensurePingPong() above
    // has created both targets — the non-null assertions are safe.
    for (let i = 0; i < activeEffects.length - 1; i++) {
      const effect = activeEffects[i];
      const target = usePing ? this.pingTarget! : this.pongTarget!;
      
      // Render to intermediate target
      // NOTE: setViewport MUST be after clear() because clear() creates new render pass
      this.ctx.setRenderTarget(target.handle);
      this.ctx.clear(0, 0, 0, 1);
      this.ctx.setViewport(0, 0, this.currentWidth, this.currentHeight);
      
      const runEffect = () => effect.render({
        ctx: this.ctx!,
        camera,
        source: currentSource,
        destination: target.handle,
        width: this.currentWidth,
        height: this.currentHeight,
        time,
        deltaTime,
        intensity: 1.0,
        isHdrTarget: true, // Intermediate targets are HDR (rgba16float)
      });
      if (profile) profile(`Render.PostProcess.${effect.type}`, runEffect);
      else runEffect();

      // CRITICAL: Flush before reading this target as texture for next effect
      if (this.ctx.flushCompute) {
        this.ctx.flushCompute();
      }

      // Swap buffers
      currentSource = target.getTexture();
      usePing = !usePing;
    }
    
    // Render last effect to final destination
    const lastEffect = activeEffects[activeEffects.length - 1];
    
    // NOTE: setViewport MUST be after clear() because clear() creates new render pass
    this.ctx.setRenderTarget(destinationTarget);
    this.ctx.clear(0, 0, 0, 1);
    this.ctx.setViewport(0, 0, this.currentWidth, this.currentHeight);
    
    const runLast = () => lastEffect.render({
      ctx: this.ctx!,
      camera,
      source: currentSource,
      destination: destinationTarget,
      width: this.currentWidth,
      height: this.currentHeight,
      time,
      deltaTime,
      intensity: 1.0,
      isHdrTarget: destinationTarget !== null, // Screen (null) = LDR, render target = HDR
    });
    if (profile) profile(`Render.PostProcess.${lastEffect.type}`, runLast);
    else runLast();
  }
  
  /**
   * Simple blit from source to target (when no effects active)
   */
  private blitToTarget(source: TextureHandle, target: RenderTargetHandle | null): void {
    if (!this.ctx) return;

    // Select pipeline based on target (null = LDR backbuffer, otherwise HDR)
    const pipeline = target !== null ? this.blitPipelineHDR : this.blitPipelineLDR;
    if (pipeline === NULL_HANDLE) {
      console.warn('[PostProcessStack] blitToTarget: blit pipeline not initialized');
      return;
    }

    this.ctx.setRenderTarget(target);
    this.ctx.clear(0, 0, 0, 1);
    this.ctx.setViewport(0, 0, this.currentWidth, this.currentHeight);

    this.ctx.setPipeline(pipeline);
    this.ctx.bindTexture(0, source);
    this.ctx.setUniform('uOpacity', 1.0);
    this.ctx.draw(3);
  }
  
  /**
   * Sort effects by order
   */
  private sortEffects(): void {
    this.effects.sort((a, b) => a.order - b.order);
  }
  
  /**
   * Serialize stack to JSON
   */
  serialize(): Record<string, unknown>[] {
    return this.effects.map(e => e.serialize());
  }
  
  /**
   * Dispose all resources
   */
  dispose(): void {
    if (this.ctx) {
      for (const effect of this.effects) {
        effect.dispose(this.ctx);
      }

      // Destroy blit resources
      if (this.blitPipelineLDR !== NULL_HANDLE) {
        this.ctx.destroyPipeline(this.blitPipelineLDR);
        this.blitPipelineLDR = NULL_HANDLE;
      }
      if (this.blitPipelineHDR !== NULL_HANDLE) {
        this.ctx.destroyPipeline(this.blitPipelineHDR);
        this.blitPipelineHDR = NULL_HANDLE;
      }
      if (this.blitShader !== NULL_HANDLE) {
        this.ctx.destroyShader(this.blitShader);
        this.blitShader = NULL_HANDLE;
      }
    }
    
    this.pingTarget?.destroy();
    this.pongTarget?.destroy();
    this.pingTarget = null;
    this.pongTarget = null;
    this.effects = [];
    this.ctx = null;
  }
}
